import get from 'lodash/get';
import isPlainObject from 'lodash/isPlainObject';
import React from 'react';
import Logger from 'observability/Logger';
import { getInstance as getDynamicReducerInstance } from 'lib/DynamicReducer/singleton';

/** @var {string} - namespace where interfaces are exposed */
const GLOBAL_NAMESPACE = 'SPLASH';
const LOG = new Logger('ComponentInterfaceManager');

/**
 * Component Interface Manager
 * ===========================
 *
 * There are cases where we want to use React/Redux in the legacy codebase but can't introduce a
 * top-level React application. This creates a home for those components and provides a place for
 * those components to render and the connection to the storem allowing them to work both within
 * the React entrypoint's application context and alongside existing legacy code.
 *
 * This includes registering the reducer namespace and any entity reducers, creating the component
 * container class, and generating the interface that is exposed on the SPLASH namespace.
 */
export default class ComponentInterfaceManager {
    /**
     * Creates a component interface manager
     *
     * @param  {Store} options.store - React store
     * @param  {Object} [options.interfaceOptions] - any additional options to pass to the interface
     *         creator functions in addition to the store
     */
    constructor({ store, interfaceOptions = {} }) {
        this.store = store;

        this._interfaceOptions = interfaceOptions;
        this._interfaces = {};
        this._components = {};
        this._componentChildren = [];
    }

    /**
     * Prepends the namespace to a key to create an exposer path
     *
     * @param  {string} key - key to prefix
     * @return {string} namespaced key (eg. "namespace.key")
     */
    namespaceKey(key) {
        return `${GLOBAL_NAMESPACE}.${key}`;
    }

    /**
     * Gets the options to pass to the interface creator
     *
     * @param  {Object} options - option overrides
     * @return {Object} merged options
     */
    getInterfaceOptions(options) {
        return {
            ...this._interfaceOptions,
            ...options,
            componentInterfaceManager: this,
        };
    }

    /**
     * Gets all registered React components for rendering
     *
     * @return {ReactElement[]} the registered compoennts
     */
    get componentChildren() {
        return this._componentChildren;
    }

    /**
     * Gets all component classes that have been registered
     *
     * @return {Object} components
     */
    get components() {
        return { ...this._components };
    }

    /**
     * Gets all component interfaces that have been registered
     *
     * @return {Object} component interfaces
     */
    get interfaces() {
        return { ...this._interfaces };
    }

    /**
     * @return {Object} - redux store
     */
    getStore() {
        return this.store;
    }

    /**
     * Gets a registered component by name
     *
     * @param  {string} name - component name
     * @return {Component} component
     */
    getComponent(name) {
        return this._components[name];
    }

    /**
     * Safely gets a component interface or a path within an interface
     *
     * @param  {string} name - component name
     * @param  {string} [path] - path on the interface
     * @param  {*} [fallback] - fallback value to use if the path is missing
     * @return {*} interface or object at a path on the interface
     */
    getInterface(name, path, fallback) {
        const componentInterface = this._interfaces[name];
        return path == null ? componentInterface : get(componentInterface, path, fallback);
    }

    /**
     * Registers a component interface on this Manager
     *
     * @param  {function} interfaceCreator - creates the interface that you're trying to register.
     *         The returned object specifies a React Component, a namespaced reducer, an entity
     *         reducer, entities, and an interface that gets exposed globally
     * @param  {string} [namespace] - namespace where the interface is exposed globally. This is
     *         only optional if the interface does not have a Component
     * @param  {Object} [options] - option overrides passed to the interfaceCreator
     * @return {Object} the component interface
     */
    register(interfaceCreator, namespace, options) {
        const interfaceOptions = this.getInterfaceOptions(options);

        if (this.getInterface(namespace)) {
            return this.getInterface(namespace);
        }

        const createdInterface = interfaceCreator(this.store, interfaceOptions);
        if (createdInterface == null) {
            return null;
        } else if (!isPlainObject(createdInterface)) {
            LOG.error(`ComponentInterfaceManager ${namespace} interface needs to be an object`);
            return null;
        }

        const {
            Component,
            reducerNamespace,
            reducer,
            epics,
            entityReducer,
            entityKeys,
            defaultEntities,
            componentInterface,
            ...rest
        } = createdInterface;

        if (Object.keys(rest).length) {
            LOG.warn(
                `${namespace} was registered with unknown keys. Did you place properties to be exposed outside 'componentInterface'?`,
                Object.keys(rest),
            );
        }

        const { mountComponent = true } = interfaceOptions;

        if (Component) {
            if (!namespace) {
                throw new Error('Must provide a component interface namespace!');
            } else if (namespace in this._components) {
                throw new Error(`"${namespace}" component namespace has already been declared!`);
            }
            this._components[namespace] = Component;
            if (mountComponent) {
                this._componentChildren.push(<Component key={namespace} />);
            }
        }

        if (namespace && componentInterface !== false) {
            this._interfaces[namespace] = componentInterface;
        }

        // Register reducer in store.
        if (reducer) {
            this.registerNamespace(reducerNamespace || namespace, reducer);
        }

        // Add epics
        if (epics) {
            this.registerEpics(epics);
        }

        // Register entity reducer in store.
        if (entityReducer) {
            if (!entityKeys) {
                LOG.warn(
                    `Missing entity keys in component interface for ${namespace}. Did you forget to export 'entityKeys' in the creator?`,
                );
            }
            this.registerEntityReducer(entityReducer, entityKeys, defaultEntities);
        }

        return componentInterface;
    }

    registerEpics(epics) {
        const dynamicReducer = getDynamicReducerInstance();
        if (dynamicReducer.epic$ && dynamicReducer.epic$.next) {
            dynamicReducer.epic$.next(epics);
        }
    }

    /**
     * Registers a namespace on behalf of a reducer and updates the state.
     * @param {string} name
     * @param {function} reducer
     */
    registerNamespace(name, reducer) {
        const dynamicReducer = getDynamicReducerInstance();
        dynamicReducer.registerNamespace(name, reducer);
        this.store.replaceReducer(dynamicReducer.createReducer());
    }

    /**
     * Registers an entity reducer to handle can handle special entity merging behavior
     * @param {function} reducer
     * @param {Object} entities
     * @param {Object} defaultEntities
     */
    registerEntityReducer(reducer, entities, defaultEntities) {
        const dynamicReducer = getDynamicReducerInstance();
        dynamicReducer.registerEntityReducer(reducer, entities, defaultEntities);
        this.store.replaceReducer(dynamicReducer.createReducer());
    }
}
